"use client"

import { BlokProps } from "@helpers/StoryblokHelpers"
import { VaccinationFormSectionStoryblok, VaccineTypeStoryblok } from "@storyblok-types"
import { SbBlokData, storyblokEditable } from "@storyblok/react/rsc"

import type { FormFieldConfig } from "@ui-library/form/CustomForm"
import Col from "@ui-library/grid/Col"
import Row from "@ui-library/grid/Row"
import GridWrapper from "@ui-library/grid/Wrapper"
import Richtext from "@ui-library/richtext/Richtext"
import { H2 } from "@ui-library/typography/Headings"
import dynamic from "next/dynamic"
import { useMemo } from "react"

const CustomForm = dynamic(() => import("@ui-library/form/CustomForm"), {
  ssr: false
})

/**
 * Value and label are the same string on purpose. These only ever travel into the notification
 * e-mail for a person to read, so an enum would mean carrying a translation table through two
 * services just to turn "onsite" back into Swedish. The vaccine select works the same way.
 */
const VACCINATION_LOCATIONS = [
  { value: "Vaccination på plats", label: "Vaccination på plats" },
  { value: "Vaccination på våra mottagningar", label: "Vaccination på våra mottagningar" }
]

export const CooperationPartnersVaccinationFormSection = ({
  blok,
  vaccines
}: BlokProps<VaccinationFormSectionStoryblok> & { vaccines?: VaccineTypeStoryblok[] }) => {
  const fields = useMemo<FormFieldConfig[]>(
    () => [
      { name: "companyName", label: "Företag", required: true, halfWidth: true },
      { name: "orgNumber", label: "Organisationsnummer", required: true, halfWidth: true },
      { name: "address", label: "Adress" },
      { name: "contactPerson", label: "Kontaktperson", required: true, halfWidth: true },
      { name: "email", label: "E-post", type: "email", required: true, halfWidth: true },
      { name: "phonenumber", label: "Telefonnummer", type: "tel", required: true, halfWidth: true },
      { name: "numberOfEmployees", label: "Antal anställda", type: "number", halfWidth: true },
      {
        name: "numberToVaccinate",
        label: "Antal anställda som ska vaccineras",
        type: "number",
        required: true,
        halfWidth: true
      },
      {
        name: "vaccineType",
        label: "Vaccin",
        type: "select",
        placeholder: "Välj vaccin",
        required: true,
        options: (vaccines ?? []).map(vaccine => ({ value: vaccine.name, label: vaccine.name }))
      },
      {
        name: "vaccinationLocation",
        label: "Vart ska vaccinationen utföras?",
        type: "select",
        placeholder: "Välj plats",
        required: true,
        options: VACCINATION_LOCATIONS
      },
      { name: "message", label: "Meddelande", type: "textarea" }
    ],
    [vaccines]
  )

  return (
    <GridWrapper {...storyblokEditable(blok as SbBlokData)} className="bg-surface">
      <Row justifyContent="center" justifyContentMobile="start">
        {blok.content && (
          <Col grid={{ xs: 12, lg: 10, xl: 8 }} className="mb-6 lg:mb-8 lg:text-center">
            <H2>{blok.title}</H2>
            <Richtext content={blok.content} />
          </Col>
        )}
        <Col grid={{ xs: 12, lg: 10, xl: 8 }}>
          <CustomForm
            recipient={blok.form_recipient}
            formName={blok.form_name}
            fields={fields}
            onSubmit={async payload => {
              const response = await fetch("/api/contact", {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(payload)
              })
              // CustomForm shows the error dialog on a rejection, so a failed send has to throw
              // rather than return — fetch only rejects on a network error, never on a 4xx or 5xx.
              if (!response.ok) throw new Error(`Contact form failed with ${response.status}`)
            }}
          />
        </Col>
      </Row>
    </GridWrapper>
  )
}

export default CooperationPartnersVaccinationFormSection
